import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO_IMAGE } from "@/lib/site";
import BookButton from "@/components/site/BookButton";

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#020617]/85 backdrop-blur-xl border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="header-logo"
                    className="flex items-center gap-3 group"
                    aria-label="90 Day Alpha — Home"
                >
                    <img
                        src={LOGO_IMAGE}
                        alt="90 Day Alpha"
                        className="h-14 sm:h-16 w-auto drop-shadow-[0_0_18px_rgba(30,144,255,0.45)] transition-all duration-300 group-hover:drop-shadow-[0_0_28px_rgba(0,183,255,0.7)]"
                    />
                    <span className="sr-only">90 Day Alpha</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label
                                .toLowerCase()
                                .replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-sm uppercase tracking-[0.18em] font-semibold transition-colors ${
                                    isActive
                                        ? "text-[#D4AF37]"
                                        : "text-white/75 hover:text-white"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:block">
                    <BookButton
                        testId="header-book-cta"
                        label="Book Free Consultation"
                    />
                </div>

                <button
                    type="button"
                    aria-label="Toggle menu"
                    data-testid="header-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden text-white p-2 -mr-2"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden border-t border-white/5 bg-[#020617]/95 backdrop-blur-xl"
                >
                    <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`mobile-nav-link-${l.label
                                    .toLowerCase()
                                    .replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `py-3 text-base uppercase tracking-[0.18em] font-semibold border-b border-white/5 ${
                                        isActive
                                            ? "text-[#D4AF37]"
                                            : "text-white/80"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <div className="mt-5">
                            <BookButton
                                testId="mobile-book-cta"
                                label="Book Free Consultation"
                                fullWidth
                            />
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
